import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'

/**
 * 餐厅信息接口
 */
export interface RestaurantInfo {
  restaurantId: string
  projectId: string
  name: string
  dbaSlogan: string
  logo: string
  street: string
  city: string
  state: string
  zipcode: string
  phoneNumber: string
  address: string
  [key: string]: any
}

/**
 * 用户完整信息接口(包含餐厅列表)
 */
interface UserInfoWithRestaurants {
  restaurantInfo: RestaurantInfo[]
  [key: string]: any
}

const STORAGE_KEY_USERINFO = 'userInfo'
const STORAGE_KEY_SELECTED_ID = 'selectedRestaurantId'

// 全局响应式状态
const restaurantList = ref<RestaurantInfo[]>([])
const selectedRestaurant = ref<RestaurantInfo | null>(null)

/**
 * 餐厅信息管理Hook
 * 
 * 职责:
 * - 登录时保存餐厅列表到localStorage
 * - 提供餐厅列表查询
 * - 管理当前选中餐厅
 * - 退出登录时清除所有餐厅数据
 */
export function useRestaurant() {
  /**
   * 从localStorage初始化餐厅数据
   * 自动加载用户餐厅列表和已选中的餐厅
   */
  const loadRestaurantData = () => {
    try {
      // 读取用户信息
      const userInfoStr = localStorage.getItem(STORAGE_KEY_USERINFO)
      if (!userInfoStr) {
        restaurantList.value = []
        selectedRestaurant.value = null
        return
      }

      const userInfo: UserInfoWithRestaurants = JSON.parse(userInfoStr)
      
      // 提取餐厅列表
      if (Array.isArray(userInfo.restaurantInfo) && userInfo.restaurantInfo.length > 0) {
        restaurantList.value = userInfo.restaurantInfo
        
        // 尝试恢复已选中的餐厅
        const savedRestaurantId = localStorage.getItem(STORAGE_KEY_SELECTED_ID)
        if (savedRestaurantId) {
          const found = restaurantList.value.find(r => r.restaurantId === savedRestaurantId)
          if (found) {
            selectedRestaurant.value = found
          } else {
            // 如果缓存的ID无效,清除
            localStorage.removeItem(STORAGE_KEY_SELECTED_ID)
          }
        }
        
        // 如果只有1个餐厅且未选中,自动选择
        if (restaurantList.value.length === 1 && !selectedRestaurant.value) {
          selectRestaurant(restaurantList.value[0])
        }
      } else {
        restaurantList.value = []
        selectedRestaurant.value = null
      }
    } catch (error) {
      console.error('加载餐厅数据失败:', error)
      restaurantList.value = []
      selectedRestaurant.value = null
    }
  }

  /**
   * 登录成功后保存用户信息(包含餐厅列表)
   * @param userInfo - 完整的用户信息对象
   */
  const saveUserInfo = (userInfo: UserInfoWithRestaurants) => {
    try {
      localStorage.setItem(STORAGE_KEY_USERINFO, JSON.stringify(userInfo))
      loadRestaurantData()
    } catch (error) {
      console.error('保存用户信息失败:', error)
      ElMessage.error('保存用户信息失败')
    }
  }

  /**
   * 选择餐厅
   * @param restaurant - 要选择的餐厅对象
   */
  const selectRestaurant = (restaurant: RestaurantInfo) => {
    selectedRestaurant.value = restaurant
    localStorage.setItem(STORAGE_KEY_SELECTED_ID, restaurant.restaurantId)
  }

  /**
   * 退出登录,清除所有餐厅数据
   */
  const clearRestaurantData = () => {
    restaurantList.value = []
    selectedRestaurant.value = null
    localStorage.removeItem(STORAGE_KEY_USERINFO)
    localStorage.removeItem(STORAGE_KEY_SELECTED_ID)
  }

  /**
   * 获取当前选中餐厅的logo
   * 如果logo为空或加载失败,返回默认logo路径
   */
  const getCurrentLogo = computed(() => {
    return selectedRestaurant.value?.logo || ''
  })

  /**
   * 获取当前选中餐厅的名称
   */
  const getCurrentName = computed(() => {
    return selectedRestaurant.value?.name || '请选择餐厅'
  })

  /**
   * 是否已选择餐厅
   */
  const hasSelected = computed(() => {
    return !!selectedRestaurant.value
  })

  /**
   * 是否有多个餐厅可选
   */
  const hasMultipleRestaurants = computed(() => {
    return restaurantList.value.length > 1
  })

  return {
    // 状态
    restaurantList,
    selectedRestaurant,
    
    // 计算属性
    getCurrentLogo,
    getCurrentName,
    hasSelected,
    hasMultipleRestaurants,
    
    // 方法
    loadRestaurantData,
    saveUserInfo,
    selectRestaurant,
    clearRestaurantData
  }
}
